import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { X, AlertCircle, EyeOffIcon, ImageIcon } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { SettingsIcon } from '@/components/icons/settings-icon';
import { BoltIcon } from '@/components/icons/bolt-icon';
import { Tooltip } from '@nextui-org/react';
import { Switch, Slider } from '@nextui-org/react';
import { cn } from '@/lib/utils';
import { logoPlaceholder } from '@/utils/placeholders';
import { useBreakpoint } from '@/lib/hooks/use-breakpoint';
import { aspectRatios } from '@/components/ui/image-size';
import { LoraModel } from '@/types/lora';
import { TriggerWordsGuide } from '../generate-sub/trigger-words-guide';
import Pbucks from '@/assets/images/P-Bucks.svg';

interface PromptSectionProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  handleGenerate: (numImages: number) => void;
  isGenerating: boolean;
  currentRatio: (typeof aspectRatios)[0];
  selectedLoraModel: LoraModel | null;
  setSelectedLoraModel: (model: LoraModel | null) => void;
  missingTriggerWords: string[];
  setIsAdvancedModalOpen: (isOpen: boolean) => void;
  handleResetSettings: () => void;
  validatePrompt: (prompt: string) => boolean;
  requiredTriggers: string[];
  stealthMode: boolean;
  setStealthMode: (value: boolean) => void;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  numImages: number;
  setNumImages: (value: number) => void;
}

export const PromptSection: React.FC<PromptSectionProps> = ({
  prompt,
  setPrompt,
  handleGenerate,
  isGenerating,
  currentRatio,
  selectedLoraModel,
  setSelectedLoraModel,
  missingTriggerWords,
  setIsAdvancedModalOpen,
  handleResetSettings,
  validatePrompt,
  requiredTriggers,
  stealthMode,
  setStealthMode,
  textareaRef,
  numImages,
  setNumImages,
}) => {
  const [showTriggerAlert, setShowTriggerAlert] = useState(false);
  const [showButtonAlert, setShowButtonAlert] = useState(false);
  const [localPrompt, setLocalPrompt] = useState(prompt);
  const breakpoint = useBreakpoint();

  const debouncedValidate = useDebounce((value: string) => {
    setPrompt(value);
    validatePrompt(value);
  }, 100);

  useEffect(() => {
    setLocalPrompt(prompt);
  }, [prompt]);

  const handleGenerateClick = () => {
    if (missingTriggerWords.length > 0) {
      setShowTriggerAlert(true);
      setShowButtonAlert(true);
      setTimeout(() => {
        setShowTriggerAlert(false);
        setShowButtonAlert(false);
      }, 2000);
      return;
    }
    handleGenerate(numImages);
  };

  return (
    <div className="mb-4 flex flex-col space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
        <div className="flex flex-1 flex-col gap-4">
          <textarea
            ref={textareaRef}
            className="h-32 w-full resize-none rounded-md border border-gray-200 bg-default-400/20 p-3 text-sm text-dark duration-200 transition-colors hover:bg-default-200 focus:border-brand focus:ring-brand dark:border-dark-400 dark:bg-dark-300/20 dark:text-light-400 dark:hover:bg-dark-300/30"
            placeholder="Enter a prompt"
            value={localPrompt}
            onChange={(e) => {
              setLocalPrompt(e.target.value);
              debouncedValidate(e.target.value);
            }}
            onFocus={() => {
              if (breakpoint === 'xs' || breakpoint === 'sm') {
                setTimeout(() => {
                  textareaRef?.current?.scrollIntoView({
                    behavior: 'instant',
                    block: 'start',
                  });
                  window.scrollBy(0, -100);
                }, 100);
              }
            }}
          />

          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <button
                className={`group relative h-14 w-full rounded-lg px-4 py-2 ${
                  isGenerating
                    ? 'cursor-not-allowed bg-gray-400 dark:bg-dark-500'
                    : 'bg-brand hover:bg-brand-dark'
                } flex items-center gap-2 text-white duration-300 transition-all`}
                onClick={handleGenerateClick}
                disabled={isGenerating}
              >
                <div className="relative z-10 flex w-full items-center justify-between px-4">
                  <div className="flex items-center gap-3">
                    <BoltIcon className="h-5 w-5" />
                    <span className="font-medium">
                      {isGenerating ? 'Generating...' : 'Generate Now'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5">
                    <Image
                      src={Pbucks}
                      width={16}
                      height={16}
                      alt="P-Bucks"
                      className="h-4 w-4"
                    />
                    <span>
                      {numImages *
                        (process.env.NEXT_PUBLIC_CREDITS_PER_IMAGE as any)}
                    </span>
                  </div>
                </div>
              </button>

              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-default-400/20 p-1.5 dark:border-dark-400 dark:bg-dark-300/20">
                <div className="flex items-center gap-1.5 rounded-lg bg-gray-50 px-2 py-1 dark:bg-dark-400">
                  <ImageIcon className="h-4 w-4 text-gray-600 dark:text-light-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-light-400">
                    {numImages}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => setNumImages(Math.min(4, numImages + 1))}
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 transition-colors dark:border-dark-400',
                      numImages >= 4
                        ? 'cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-dark-400 dark:text-dark-600'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-400 dark:text-light-400 dark:hover:bg-dark-300'
                    )}
                    disabled={numImages >= 4}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2002000/svg"
                    >
                      <path
                        d="M6 2.5V9.5M2.5 6H9.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setNumImages(Math.max(1, numImages - 1))}
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 transition-colors dark:border-dark-400',
                      numImages <= 1
                        ? 'cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-dark-400 dark:text-dark-600'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-400 dark:text-light-400 dark:hover:bg-dark-300'
                    )}
                    disabled={numImages <= 1}
                  >
                    <svg
                      width="12"
                      height="2"
                      viewBox="0 0 12 2"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1H11"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-2 rounded-md border border-gray-200 bg-default-400/20 p-2 dark:border-dark-400 dark:bg-dark-300/20 xl:min-w-[180px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-600 dark:text-light-400">
              {currentRatio?.name}
            </span>
            {selectedLoraModel && (
              <>
                <span className="text-xs text-gray-400 dark:text-dark-600">
                  •
                </span>
                <div className="flex items-center gap-1">
                  <Image
                    src={selectedLoraModel?.image ?? logoPlaceholder}
                    alt={selectedLoraModel.name}
                    width={12}
                    height={12}
                    className="rounded-full"
                  />
                  <span className="max-w-[100px] truncate text-xs text-gray-600 dark:text-light-400">
                    {selectedLoraModel.name}
                  </span>
                  <button
                    onClick={() => setSelectedLoraModel(null)}
                    className="ml-0.5 text-gray-400 duration-200 transition-colors hover:text-gray-600 dark:text-dark-600 dark:hover:text-light-400"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 pt-2 dark:border-dark-400">
            <button
              onClick={handleResetSettings}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-dark-600 dark:hover:text-light-400"
            >
              Reset
            </button>
            <button
              onClick={() => setIsAdvancedModalOpen(true)}
              className="rounded-md bg-gray-100 p-1.5 duration-200 transition-colors hover:bg-gray-200 dark:bg-dark-400 dark:hover:bg-dark-300"
            >
              <SettingsIcon className="h-4 w-4 text-gray-600 dark:text-light-400" />
            </button>
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 pt-2 dark:border-dark-400">
            <div className="flex items-center gap-2">
              <EyeOffIcon
                className={cn(
                  'h-3.5 w-3.5 duration-200 transition-colors',
                  stealthMode
                    ? 'text-brand'
                    : 'text-gray-500 dark:text-dark-600'
                )}
              />
              <span
                className={cn(
                  'text-xs duration-200 transition-colors',
                  stealthMode
                    ? 'font-medium text-brand'
                    : 'text-gray-600 dark:text-light-400'
                )}
              >
                Stealth Mode
              </span>
            </div>
            <Tooltip
              content={
                stealthMode
                  ? 'Images will be private by default'
                  : 'Image will be public by default'
              }
            >
              <div>
                <Switch
                  size="sm"
                  color={stealthMode ? 'primary' : 'default'}
                  checked={stealthMode}
                  isSelected={stealthMode}
                  onChange={(e) => setStealthMode(e.target.checked)}
                />
              </div>
            </Tooltip>
          </div>
        </div>
      </div>

      <div
        className={`transform duration-300 transition-all
        ${
          showTriggerAlert ? 'scale-105 animate-bounce-horizontal' : 'scale-100'
        }`}
      >
        <TriggerWordsGuide
          requiredTriggers={requiredTriggers}
          missingTriggerWords={missingTriggerWords}
          handleTriggerClick={(trigger) => {
            if (!prompt.toLowerCase().includes(trigger.toLowerCase())) {
              const newPrompt =
                prompt.trim() + (prompt.trim() ? ' ' : '') + trigger;
              setPrompt(newPrompt);
              validatePrompt(newPrompt);
            }
          }}
        />
      </div>
    </div>
  );
};
